import React, { useState, useEffect, useRef } from "react";
import { Calendar } from "lucide-react";
import { getDateFormat, subscribeDateFormat } from "@/lib/api";

/**
 * User-preferred date-format text input (mm/dd/yyyy, dd/mm/yyyy, or
 * yyyy-mm-dd) that binds an ISO (YYYY-MM-DD) value externally.
 * Renders a hidden native `<input type="date">` so the calendar icon opens
 * the native picker.
 */
function _parts(iso) {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  return { y: m[1], mo: m[2], d: m[3] };
}

function formatForFmt(iso, fmt) {
  const p = _parts(iso);
  if (!p) return "";
  switch (fmt) {
    case "dd/mm/yyyy": return `${p.d}/${p.mo}/${p.y}`;
    case "yyyy-mm-dd": return `${p.y}-${p.mo}-${p.d}`;
    case "mm/dd/yyyy":
    default: return `${p.mo}/${p.d}/${p.y}`;
  }
}

function parseInputToIso(v, fmt) {
  if (!v) return "";
  const t = v.trim();
  let mo, d, y;
  if (fmt === "yyyy-mm-dd") {
    const m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(t);
    if (!m) return null;
    [, y, mo, d] = m;
  } else if (fmt === "dd/mm/yyyy") {
    const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(t);
    if (!m) return null;
    [, d, mo, y] = m;
  } else {
    const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(t);
    if (!m) return null;
    [, mo, d, y] = m;
  }
  const iy = parseInt(y, 10);
  const imo = parseInt(mo, 10);
  const idd = parseInt(d, 10);
  if (imo < 1 || imo > 12 || idd < 1 || idd > 31 || iy < 1900 || iy > 2999) return null;
  const iso = `${y}-${String(imo).padStart(2, "0")}-${String(idd).padStart(2, "0")}`;
  const parsed = new Date(iso + "T00:00:00");
  if (isNaN(parsed) || parsed.getMonth() + 1 !== imo || parsed.getDate() !== idd) return null;
  return iso;
}

export default function DateInput({
  value,
  onChange,
  className = "",
  required = false,
  disabled = false,
  id,
  ...rest
}) {
  const [fmt, setFmt] = useState(getDateFormat());
  const [text, setText] = useState(formatForFmt(value, fmt));
  const [invalid, setInvalid] = useState(false);
  const nativeRef = useRef(null);

  // Follow app-wide format changes (e.g. after user updates preference)
  useEffect(() => subscribeDateFormat(setFmt), []);

  // Sync external value / format → text
  useEffect(() => {
    setText(formatForFmt(value, fmt));
    setInvalid(false);
  }, [value, fmt]);

  const commit = (v) => {
    if (!v) {
      setInvalid(required);
      onChange("");
      return;
    }
    const iso = parseInputToIso(v, fmt);
    if (iso) {
      setInvalid(false);
      onChange(iso);
    } else {
      setInvalid(true);
    }
  };

  const openPicker = () => {
    const el = nativeRef.current;
    if (!el) return;
    if (typeof el.showPicker === "function") {
      try { el.showPicker(); return; } catch { /* fall through */ }
    }
    el.focus();
    el.click();
  };

  const placeholder = fmt === "yyyy-mm-dd" ? "yyyy-mm-dd" : fmt;

  return (
    <div className={`relative ${className}`}>
      <input
        type="text"
        inputMode="numeric"
        placeholder={placeholder}
        value={text}
        required={required}
        disabled={disabled}
        id={id}
        onChange={(e) => {
          setText(e.target.value);
          if (invalid) setInvalid(false);
        }}
        onBlur={() => commit(text)}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit(text);
        }}
        aria-invalid={invalid || undefined}
        className={`w-full bg-white border rounded-lg pl-3 pr-9 py-2 font-mono text-sm focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none ${
          invalid ? "border-destructive" : "border-border"
        }`}
        {...rest}
      />
      <button
        type="button"
        onClick={openPicker}
        disabled={disabled}
        tabIndex={-1}
        aria-label="Open date picker"
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-ink-lighter hover:text-ink"
      >
        <Calendar className="w-4 h-4" strokeWidth={1.75} />
      </button>
      <input
        ref={nativeRef}
        type="date"
        value={value || ""}
        onChange={(e) => {
          setText(formatForFmt(e.target.value, fmt));
          setInvalid(false);
          onChange(e.target.value);
        }}
        tabIndex={-1}
        aria-hidden="true"
        className="absolute opacity-0 pointer-events-none w-0 h-0"
      />
    </div>
  );
}
